import { type BuildAndBuyDispatchType, useBnbSelector } from "../../../redux/store";
import type {
    AccentColourType,
    ColourType,
    ExtraType,
    PackType,
    PriceType,
    UpholsteryType,
    WheelType,
} from "../../../../../../common-deprecated/types/CommonTypes";
import { find } from "../../../../../../common-deprecated/utils";
import {
    addPack,
    addPackExtra,
    removePack,
    removePackExtra,
    setPrimaryAccentColour,
    setSecondaryAccentColour,
} from "../../../redux/actions/BasketActions";
import { getPackItems } from "../../../../../shared-logic/utils/packUtils";
import Debug from "../../../../../../common-deprecated/Debug";
import { useDispatch } from "../../../../../../shared-logic/redux/store";

type UsePackType = {
    actions: {
        selectPack: () => void;
        deselectPack: () => void;
        selectPrimaryAccentColour: (colour: AccentColourType) => void;
        selectSecondaryAccentColour: (colour: AccentColourType) => void;
        equipmentSelect: (equipment: ExtraType) => void;
        equipmentDeselect: (equipment: ExtraType) => void;
    };
    packContent: {
        selected: boolean;
        enabled: boolean;
        price: PriceType;
        primaryAccentColour: AccentColourType | null;
        primaryAccentColours: AccentColourType[];
        secondaryAccentColour: AccentColourType | null;
        secondaryAccentColours: AccentColourType[];
        selectedEquipment: string[];
        optionalEquipment: ExtraType[];
        colours: ColourType[];
        upholsteries: (UpholsteryType | ExtraType)[];
        wheels: WheelType[];
        renderedEquipment: (UpholsteryType | ExtraType | WheelType)[];
        totalPackContent: number;
    };
};

const usePack = (pack: PackType): UsePackType | null => {
    const query = useBnbSelector((state) => state.commonSettings.query);
    const basket = useBnbSelector((state) => state.basket);
    const items = useBnbSelector((state) => state.items);
    const financeOption = useBnbSelector((state) => state.utils.financeOption);
    const dispatch = useDispatch<BuildAndBuyDispatchType>();

    const isSelected = !!find(pack.id, basket.packs);
    const selectedExtras = basket.packExtras.map((extra) => extra.id);
    const packSelected = pack.standard || isSelected;

    const packContent = getPackItems(pack, basket, items.upholsteries, financeOption);

    // This is done here and not in the formatters as otherwise it might interfere with the include/exclude rules
    if (
        packContent.renderedEquipment.length <= 0 &&
        packContent.optionalEquipment.length <= 0 &&
        packContent.wheels.length <= 0 &&
        packContent.upholsteries.length <= 0 &&
        packContent.colours.length <= 0
    ) {
        Debug.warn(`Pack ${pack.name} doesn't have any items and is not shown`, {
            path: query.path,
            packId: pack.id,
            status: "warning",
        });
        return null;
    }

    // Redux select actions.
    const actions = {
        selectPack: (): void => {
            dispatch(addPack(pack, [], [], false, false, true, true));
        },
        deselectPack: (): void => {
            dispatch(removePack(pack, true));
        },
        selectPrimaryAccentColour: (colour: AccentColourType): void => {
            dispatch(setPrimaryAccentColour(pack, colour, false, true, true));
        },
        selectSecondaryAccentColour: (colour: AccentColourType): void => {
            dispatch(setSecondaryAccentColour(pack, colour, false, true, true));
        },
        equipmentSelect: (equipment: ExtraType): void => {
            dispatch(addPackExtra(pack, equipment, [], false, true, false));
        },
        equipmentDeselect: (equipment: ExtraType): void => {
            dispatch(removePackExtra(pack, equipment, true));
        },
    };

    return {
        actions,
        packContent: {
            ...packContent,
            enabled: packContent.availability.enabled,
            selected: packSelected,
            selectedEquipment: isSelected ? selectedExtras : [],
        },
    };
};

export default usePack;
